import styled, { keyframes, css } from "styled-components";

const theme = {
  bg: "#f8f9fa",
  surface: "#ffffff",
  border: "#e9ecef",
  primary: "#007bff",
  secondary: "#6f42c1",
  text: "#212529",
  textSecondary: "#6c757d",
  danger: "#dc3545",
  success: "#28a745",
  warning: "#ffc107",
  shadow: "rgba(0, 0, 0, 0.05)",
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const PageWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.bg};
  animation: ${fadeIn} 0.5s ease-in-out;
`;

export const Sidebar = styled.aside`
  width: 280px;
  flex-shrink: 0;
  background-color: ${theme.surface};
  border-right: 1px solid ${theme.border};
  padding: 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  a {
    padding: 0.8rem 1rem;
    text-decoration: none;
    color: ${theme.textSecondary};
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.2s ease;
    &:hover {
      background-color: ${theme.bg};
      color: ${theme.primary};
    }
  }
`;

export const MainContent = styled.main`
  flex-grow: 1;
  padding: 3rem 4rem 3rem 6rem;
  overflow-y: auto;
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${theme.border};
`;

export const BackButton = styled.button`
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  color: ${theme.textSecondary};
  position: absolute;
  top: 20px;
  left: 30px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.8rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${theme.shadow};

  &:hover {
    background: ${theme.primary};
    color: white;
    transform: scale(1.1);
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.text};

  span {
    font-weight: 400;
    color: ${theme.textSecondary};
  }
`;

const getStatusStyles = (status) => {
  switch (status) {
    case 1:
      return css`
        background-color: rgba(40, 167, 69, 0.1);
        color: ${theme.success};
      `;
    case 2:
      return css`
        background-color: rgba(0, 123, 255, 0.1);
        color: ${theme.primary};
      `;
    case 3:
      return css`
        background-color: rgba(220, 53, 69, 0.1);
        color: ${theme.danger};
      `;
    case 4:
      return css`
        background-color: rgba(255, 193, 7, 0.1);
        color: #b08000;
      `;
    case 5:
      return css`
        background-color: rgba(111, 66, 193, 0.1);
        color: ${theme.secondary};
      `;
    default:
      return css`
        background-color: rgba(108, 117, 125, 0.1);
        color: ${theme.textSecondary};
      `;
  }
};

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.4rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1rem;
  ${({ status }) => getStatusStyles(status)}
`;

export const Section = styled.section`
  margin-bottom: 3rem;
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${theme.text};
  margin: 0 0 1.5rem 0;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
`;

export const InfoCard = styled.div`
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 6px 24px ${theme.shadow};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }
`;

export const InfoLabel = styled.span`
  font-size: 0.85rem;
  color: ${theme.textSecondary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

export const InfoValue = styled.p`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(props) => (props.highlight ? theme[props.highlight] : theme.text)};
  margin: 0;
`;

export const InfoSubValue = styled.p`
  font-size: 0.9rem;
  color: ${theme.textSecondary};
  margin: 0.25rem 0 0 0;
`;

export const FooterActions = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.border};
  display: flex;
  justify-content: flex-end;
`;

export const ActionButton = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background-color: ${theme.danger};
  color: white;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);

  &:hover {
    transform: translateY(-3px) scale(1.05);
    background-color: #c82333;
    box-shadow: 0 7px 20px rgba(220, 53, 69, 0.3);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: ${theme.textSecondary};
  font-size: 1.5rem;
  font-weight: 500;
`;

export const ReceiptButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.7rem 1.2rem;
  border-radius: 30px;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  /* Se tiver a prop outline, faz um estilo de borda, senão fundo cheio */
  ${(props) =>
    props.outline
      ? css`
          background: transparent;
          border: 2px solid ${theme.primary};
          color: ${theme.primary};
          &:hover {
            background: ${theme.primary};
            color: white;
          }
        `
      : css`
          background: ${theme.primary};
          color: white;
          border: none;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
          &:hover {
            background: #0069d9;
            transform: translateY(-2px);
          }
        `}
`;

export const GoldenButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.7rem 1.2rem;
  border-radius: 30px;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  /* Gradiente Dourado */
  background: linear-gradient(135deg, #d4af37, #b88a00);
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.6);
    background: linear-gradient(135deg, #f9d976, #d4af37);
  }
`;

export const GoldenBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.6rem 1.2rem;
  border-radius: 30px;
  font-weight: 700;
  font-size: 0.9rem;

  /* Estilo visual de "Ativado" */
  background-color: rgba(212, 175, 55, 0.1);
  color: #b88a00;
  border: 1px solid #d4af37;
  cursor: default;
`;
